import React from 'react';
import PropTypes from 'prop-types';

const START_HOUR = 8;
const END_HOUR = 18;
const TIER_COLORS = {
  'on-pace': '#34d399',
  ahead: '#34d399',
  neutral: '#fbbf24',
  behind: '#ff453a',
};

function PickingHistory({
  measuredAt, status, timeline, t,
}) {
  const measuredDate = new Date(measuredAt);
  if (!measuredAt || !Number.isFinite(measuredDate.getTime())) return null;

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(measuredDate);
  const time = Object.fromEntries(parts.map((part) => [part.type, Number(part.value)]));
  const measuredHour = time.hour + (time.minute / 60) + (time.second / 3600);
  const endHour = Math.min(END_HOUR, Math.max(START_HOUR, measuredHour));
  const duration = endHour - START_HOUR;
  const readings = timeline.filter((point) => (
    point.hour >= START_HOUR
    && point.hour <= endHour
    && Number.isFinite(point.current)
    && TIER_COLORS[point.status]
  )).sort((a, b) => a.hour - b.hour);

  // Include the latest live tier between 20-minute readings, but stop at 6 PM.
  if (measuredHour >= START_HOUR && measuredHour < END_HOUR && TIER_COLORS[status]) {
    readings.push({ hour: endHour, status });
  }

  const stops = duration > 0 ? readings.map((point) => (
    `${TIER_COLORS[point.status]} ${((point.hour - START_HOUR) / duration) * 100}%`
  )) : [];
  // A single reading still needs two stops to form a valid CSS gradient.
  if (stops.length === 1) stops.push(stops[0]);

  const throughLabel = measuredHour >= END_HOUR ? t('6 PM') : measuredDate.toLocaleTimeString('en-US', {
    timeZone: 'America/Chicago',
    hour: 'numeric',
    minute: '2-digit',
  });
  const description = duration > 0
    ? t('Picking tier history from 8 AM through {{time}}. Red, yellow, and green tiers sampled every 20 minutes blend into the latest reading. Unfilled space is the rest of the day.', { time: throughLabel })
    : t('Picking tier history from 8 AM to 6 PM. The workday has not started.');

  return (
    <div className="today-pace-sparkline today-picking-history" role="img" aria-label={description} title={description}>
      <div className="today-picking-history__heading" aria-hidden="true">
        <span>{t('Picking history')}</span>
        <span>{duration > 0 ? t('Through {{time}}', { time: throughLabel }) : t('8 AM–6 PM')}</span>
      </div>
      <div className="today-picking-history__track" aria-hidden="true">
        {stops.length > 0 && (
          <div
            className="today-picking-history__fill"
            style={{
              width: `${(duration / (END_HOUR - START_HOUR)) * 100}%`,
              '--picking-history-gradient': `linear-gradient(to right, ${stops.join(', ')})`,
            }}
          />
        )}
      </div>
      <div className="today-picking-history__hours" aria-hidden="true">
        <span>8A</span>
        <span>1P</span>
        <span>6P</span>
      </div>
    </div>
  );
}

PickingHistory.propTypes = {
  measuredAt: PropTypes.string,
  status: PropTypes.string.isRequired,
  timeline: PropTypes.arrayOf(PropTypes.shape({
    hour: PropTypes.number.isRequired,
    current: PropTypes.number,
    status: PropTypes.string,
  })),
  t: PropTypes.func.isRequired,
};

PickingHistory.defaultProps = { measuredAt: null, timeline: [] };

export default PickingHistory;
